import { useEffect, useRef } from "react";
import { DateTimeUnit } from "luxon";
import { ZoomableTimeBandAxis } from "./ZoomableTimeBandAxis";
import { resolveTimeBandAxisZoom } from "./timeBandAxisZoom";
import { ZoomData } from "@mui/x-charts/internals";

/**
 * Minimal interface for the chart API ref that supports zoom operations.
 * This avoids depending on internal MUI types that may not be exported.
 */
export interface ChartZoomApiRef {
  current:
    | {
        setAxisZoomData: (axisId: string, zoomData: ZoomData) => void;
      }
    | null
    | undefined;
}

export interface UseInitialTimeBandZoomOptions {
  /**
   * The chart API ref from useChartProApiRef
   */
  apiRef: ChartZoomApiRef;
  /**
   * The zoomable time band axis configuration containing min, max, and zoom options
   */
  zoomableAxis: ZoomableTimeBandAxis;
  /**
   * The time unit for the axis (e.g., "month", "day", "year")
   */
  timeUnit: DateTimeUnit;
  /**
   * The current width of the chart container
   */
  width: number | undefined;
  /**
   * Whether zoom is enabled for the chart
   */
  zoomEnabled: boolean;
  /**
   * The axis ID to set zoom data for. Defaults to "timeBand"
   */
  axisId?: string;
  /**
   * Where to align the initial zoom window.
   * - "start": Show the beginning of the data range (oldest data first)
   * - "end": Show the end of the data range (most recent data first)
   * Defaults to "end"
   */
  zoomAlignment?: "start" | "end";
}

/**
 * Hook that sets the initial zoom position for a time band axis chart.
 *
 * This hook calculates and sets an optimal initial zoom window based on:
 * - The total data range
 * - The chart width
 * - The minTickSize option (minimum pixels per tick for readability)
 *
 * It only sets the zoom:
 * 1. Once on initial mount
 * 2. When the axis range changes (min/max dates change)
 *
 * It does NOT interfere with user zoom/pan interactions, avoiding the
 * "wobbling" issue that can occur with controlled zoom state.
 *
 * @example
 * ```tsx
 * const apiRef = useChartProApiRef<"line" | "bar">();
 * const { ref, width } = useResizeDetector({ handleHeight: false });
 *
 * useInitialTimeBandZoom({
 *   apiRef,
 *   zoomableAxis: { min: startDate, max: endDate, zoom: { minTickSize: 75 } },
 *   timeUnit: "month",
 *   width,
 *   zoomEnabled: true,
 * });
 * ```
 */
export const useInitialTimeBandZoom = (
  options: UseInitialTimeBandZoomOptions
): void => {
  const {
    apiRef,
    zoomableAxis,
    timeUnit,
    width,
    zoomEnabled,
    axisId = "timeBand",
    zoomAlignment = "end",
  } = options;

  const initialZoomSetRef = useRef(false);
  const prevAxisRangeRef = useRef<{ min: number; max: number } | null>(null);

  useEffect(() => {
    if (!zoomEnabled || width === undefined) {
      return;
    }

    const currentAxisRange = {
      min: zoomableAxis.min.toMillis(),
      max: zoomableAxis.max.toMillis(),
    };

    // Only set zoom if:
    // 1. Initial zoom hasn't been set yet, OR
    // 2. The axis range has changed
    const axisRangeChanged =
      prevAxisRangeRef.current === null ||
      prevAxisRangeRef.current.min !== currentAxisRange.min ||
      prevAxisRangeRef.current.max !== currentAxisRange.max;

    if (!initialZoomSetRef.current || axisRangeChanged) {
      const zoom = resolveTimeBandAxisZoom(
        zoomableAxis,
        timeUnit,
        width,
        zoomAlignment
      );
      if (zoom !== undefined) {
        apiRef.current?.setAxisZoomData(axisId, {
          axisId: axisId,
          start: zoom.start,
          end: zoom.end,
        });
        initialZoomSetRef.current = true;
        prevAxisRangeRef.current = currentAxisRange;
      }
    }
  }, [
    apiRef,
    zoomableAxis,
    timeUnit,
    width,
    zoomEnabled,
    axisId,
    zoomAlignment,
  ]);
};
